import React, { useContext, useEffect, useMemo } from 'react';
import classnames from 'classnames';

import { JoinAnotherOrganization } from '../../JoinAnotherOrganization';
import { LeaveOrganization } from '../../LeaveOrganization';
import { EditableFieldsContext } from '../context';

import { OrganizationMembership } from 'common/api/crm/models/OrganizationMembership';
import { createStyles } from 'utils/createStyle';
import { useRootSelector, FeaturesState } from 'common/features/featuresReducer';
import { RootState } from 'rootStore';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { Value } from '@firebase/remote-config';
import { useSelector } from 'react-redux';

const styles = createStyles({
    container: ['flex flex-col'],
    enabledContainer: ['opacity-100'],
    disabledContainer: ['opacity-20'],
    defaultBox: [
        'border border-1 text-center px-3 rounded rounded-1 ml-2',
        {
            lineHeight: '15px',
            height: 15,
            fontSize: 10,
        },
    ],
    defaultBoxActive: ['border-brand bg-brand text-white'],
    organization: ['', { fontSize: 17 }],
    label: [
        'text-grey-2 mb-2',
        {
            fontSize: '14px',
        },
    ],
});

const useOrgMemberships = () => {
    const homeDetails = useSelector((state: FeaturesState) => state.store.home.homeDetails);
    const memberships = homeDetails?.organizations;

    return useMemo(() => {
        return Object.values(memberships || {}).filter(
            ({ isActive }: OrganizationMembership) => isActive
        );
    }, [memberships]);
};

export const DefaultOrgField = () => {
    const memberships = useOrgMemberships();
    const defaultOrgId = useRootSelector(
        (state: RootState) => state.store.home.homeDetails?.defaultOrganization?.id
    );
    const { fieldToEdit: isAnyFieldBeingEdited } = useContext(EditableFieldsContext);
    const disableLeaveOrganization = useFirebaseConfigValues<boolean>(
        'disable_leave_organization',
        false,
        (value: Value) => value.asBoolean()
    );

    const orgJoinComplete = () => null;

    // reassign the #hash to move the focus to the organizations area.
    useEffect(() => {
        if (location.hash === '#organizations') {
            location.hash = '';
            location.hash = '#organizations';
        }
    }, []);

    return (
        <div
            id="organizations"
            className={classnames(
                styles.container,
                isAnyFieldBeingEdited ? styles.disabledContainer : styles.enabledContainer
            )}
        >
            <label className={styles.label}>Your Organizations</label>
            <div className="text-lg">
                {memberships.map((membership: OrganizationMembership) => (
                    <div className="flex items-center" key={membership.organization.id}>
                        <p className={styles.organization}>{membership.organization.name}</p>
                        <p
                            className={classnames(
                                styles.defaultBox,
                                defaultOrgId === membership.organization.id
                                    ? styles.defaultBoxActive
                                    : 'hidden'
                            )}
                        >
                            Default
                        </p>
                    </div>
                ))}
            </div>
            <div>
                <JoinAnotherOrganization
                    disabled={Boolean(isAnyFieldBeingEdited)}
                    className="block relative"
                    orgJoinCompleteCallback={orgJoinComplete}
                >
                    <span className="join_another_org_cta text-brand text-xs font-bold underline">
                        Join another organization
                    </span>
                </JoinAnotherOrganization>
                {!disableLeaveOrganization && (
                    <>
                        <span className="text-grey-3"> | </span>
                        <LeaveOrganization
                            defaultOrganization={defaultOrgId as string}
                            memberships={memberships}
                        >
                            <span className="text-brand text-xs font-bold underline">
                                Leave an organization
                            </span>
                        </LeaveOrganization>
                    </>
                )}
            </div>
        </div>
    );
};
